import type {CrosspointDataService} from '../types/crosspoint-data-service.interface';
import type {CrosspointDataProvider} from '../types/crosspoint-data-provider.interface';
import type {ItemMapper} from '../types/item-mapper.interface';
import type {MatrixEntry} from '../types/matrix-item.interface';
import {Viewport} from "../types/viewport.interface";
import {ViewportItem} from "../types/viewport-item.interface";
import {CrosspointDataProviderErrorHandler} from "../types/crosspoint-data-provider-error.handler.interface";
import type {SpatialMatrix} from '../../core';
import {FetchedRegionsTracker} from '../../core';
import {Axis} from "../../axis";
import {ValueOutsideDomainError} from "../../axis/errors/value-outside-domain.error";
import {describeValue} from "../../utils/describe-value";


/**
 * Represents a standard data provider for managing and fetching crosspoint data
 * within a spatial matrix. This class handles data retrieval, caching, mapping,
 * and error handling for efficient viewport-based data access.
 *
 * @template TXValue - Type of the X-axis values.
 * @template TYValue - Type of the Y-axis values.
 * @template TItem - Type of the items managed by the data provider.
 */
export class StandardDataProvider<TXValue, TYValue, TItem> implements CrosspointDataProvider<TXValue, TYValue, TItem> {

    private fetchedRegionsTracker = new FetchedRegionsTracker();
    private inFlightRegions = new Set<string>();

    get dataProviderKey() {
        return this.key;
    }

    get axisX(): Axis<TXValue> {
        return this.spatialMatrix.getXAxis();
    }

    get axisY(): Axis<TYValue> {
        return this.spatialMatrix.getYAxis();
    }

    constructor(
        private readonly key: string,
        private spatialMatrix: SpatialMatrix<TXValue, TYValue, TItem>,
        private dataService: CrosspointDataService<TItem>,
        private itemMapper: ItemMapper<TXValue, TYValue, TItem>,
        private errorHandler: CrosspointDataProviderErrorHandler,
        private readonly onBackgroundFetchComplete?: () => void
    ) {
    }

    /**
     * Get items for a viewport (with automatic fetching)
     *
     * Algorithm:
     * 1. Check if region already fetched (via SimpleFetchedRegionsTracker)
     * 2. If not fetched, fetch from backend (via CrosspointDataService)
     * 3. Map items to matrix entries (via ItemMapper)
     * 4. Add entries to SpatialMatrix cache
     * 5. Query SpatialMatrix for viewport items
     * 6. Handle errors (via CrossPointDataProviderErrorHandler)
     *
     * @param viewport - ViewportWindow with numeric indices
     * @returns Promise with items in viewport
     */
    async getItemsForViewportAsync(viewport: Viewport): Promise<TItem[]> {

        // Query cache for viewport
        const positionedItems = await this.getViewportItemsForViewportAsync(viewport);

        // Extract unique items (deduplication for multi-assignment)
        const uniqueItems = new Map<TItem, TItem>();
        for (const positioned of positionedItems) {
            uniqueItems.set(positioned.data, positioned.data);
        }

        return Array.from(uniqueItems.values());
    }

    /**
     * Get ViewportItems for a viewport (with automatic fetching)
     *
     * Same as getItemsForViewport(), but returns ViewportItems with position data.
     * Useful for Timeline rendering where indices are needed.
     *
     * Uses FetchedRegionsTracker to intelligently fetch only missing regions.
     *
     * @param viewport - ViewportWindow with numeric indices
     * @returns Promise with ViewportItems (includes indices + clipping info)
     */
    async getViewportItemsForViewportAsync(viewport: Viewport): Promise<ViewportItem<TItem>[]> {
        // Get regions that need to be fetched (smart region tracking!)
        const unfetchedRegions = this.fetchedRegionsTracker.getUnfetchedRegions(viewport);

        // Ensure Y-axis range loaded (clipped to known boundaries)
        const yAxis = this.spatialMatrix.getYAxis();
        const [yBoundMin, yBoundMax] = yAxis.getBoundaries();
        const yMin = Math.floor(viewport.indexRangeY[0]);
        const yMax = Math.ceil(viewport.indexRangeY[1]);
        // Clip to boundaries only if they are finite (confirmed)
        const yClippedMin = Number.isFinite(yBoundMin) ? Math.max(yMin, yBoundMin) : yMin;
        const yClippedMax = Number.isFinite(yBoundMax) ? Math.min(yMax, yBoundMax) : yMax;
        if (yClippedMin <= yClippedMax) {
            await yAxis.ensureRange(yClippedMin, yClippedMax + 1);
        }

        // Ensure X-axis range loaded (clipped to known boundaries, CRITICAL for addItem!)
        const xAxis = this.spatialMatrix.getXAxis();
        const [xBoundMin, xBoundMax] = xAxis.getBoundaries();
        const xMin = Math.floor(viewport.indexRangeX[0]);
        const xMax = Math.ceil(viewport.indexRangeX[1]);
        // Clip to boundaries only if they are finite (confirmed)
        const xClippedMin = Number.isFinite(xBoundMin) ? Math.max(xMin, xBoundMin) : xMin;
        const xClippedMax = Number.isFinite(xBoundMax) ? Math.min(xMax, xBoundMax) : xMax;
        if (xClippedMin <= xClippedMax) {
            await xAxis.ensureRange(xClippedMin, xClippedMax + 1);
        }

        // Filter out regions already in-flight (duplicate prevention - AC7)
        const regionsToFetch = unfetchedRegions.filter(region => {
            const regionKey = this.generateRegionKey(region.indexRangeX, region.indexRangeY);
            return !this.inFlightRegions.has(regionKey);
        });

        // Start background fetch if there are regions to fetch
        if (regionsToFetch.length > 0) {
            // Fire-and-forget: don't await, let it run in background.
            //
            // The catch is not defensive padding. fetchRegionsInBackground rejects on a real
            // path: an error handler returning ABORT rethrows, and addItem can throw ("Values
            // not found in axis cache"). With nobody awaiting this promise, that rejection is
            // unhandled — a console-level `unhandledrejection` in the browser, and under a
            // strict Node/vitest setup enough to fail the run of an unrelated test.
            //
            // Reported through the same channel as everything else rather than swallowed. The
            // ABORT verdict itself has no addressee here — there is no caller to abort — which
            // is worth knowing when reading ErrorRecoveryAction: on the background path SKIP
            // and ABORT differ only in whether the remaining regions are attempted.
            void this.fetchRegionsInBackground(regionsToFetch).catch((error: unknown) => {
                const reported = error instanceof Error ? error : new Error(describeValue(error));
                console.warn(
                    `[CrosspointDataProvider] Background fetch aborted: ${reported.message}`
                );
                this.errorHandler.onEntriesDropped?.(regionsToFetch[0], reported);
            });
        }

        // Query cache for viewport - returns ViewportItems with indices (may be incomplete)
        return this.spatialMatrix.queryViewport(viewport);
    }

    /**
     * Fetch regions in background (sequentially)
     *
     * Fetches data for each region, adds items to SpatialMatrix, marks regions as fetched,
     * and calls callback after completion (if provided).
     *
     * Handles AbortError cleanly: no markAsFetched, no callback, cleanup in-flight set.
     * Handles other errors via ErrorHandler: SKIP continues, ABORT rethrows.
     *
     * @param regions - Array of regions to fetch
     * @private
     */
    private async fetchRegionsInBackground(regions: Viewport[]): Promise<void> {
        let anySuccessfulFetch = false;

        for (const region of regions) {
            // Add region to in-flight set BEFORE fetch (AC7)
            const regionKey = this.generateRegionKey(region.indexRangeX, region.indexRangeY);
            this.inFlightRegions.add(regionKey);

            try {
                const items = await this.dataService.fetchData(region);

                // Map items to matrix entries
                const entries = items.flatMap(item => this.itemMapper.mapToMatrixEntries(item));

                // Ensure all axis values are loaded before adding items. Returns only the
                // entries that are safe to add — entries whose axis value cannot be loaded
                // (the axis service lacks expandToValue) are dropped so they never reach
                // addItem (which would throw "Values not found in axis cache").
                const {safeEntries, hadUnexpectedFailure} =
                    await this.ensureIndicesComplete(entries, region);

                // Add to cache
                for (const entry of safeEntries) {
                    this.spatialMatrix.addItem(
                        entry.id,
                        entry.spanX,
                        entry.indexRangeY,
                        entry.data
                    );
                }

                // Mark region as fetched — but NOT when a load failed for an unexpected
                // reason. A value genuinely outside the axis' domain means the region really is
                // complete without it. A backend that was unreachable means it is not, and
                // marking it would lose those items for the whole session, because
                // getUnfetchedRegions() would never return this region again.
                //
                // THE COMPLETION CALLBACK FOLLOWS THE SAME CONDITION, and that is the whole
                // point rather than tidiness. The callback exists to tell the host new data
                // arrived so it re-renders; a re-render issues a fresh viewport query; a fresh
                // query asks getUnfetchedRegions(), which still returns this region because it
                // was deliberately not marked. Firing the callback here therefore does not
                // merely permit a retry — it CAUSES one, immediately, with no user action. For
                // a deterministic failure (a backend answering 500 for one value) that is a
                // request plus a full re-render per host update cycle, without end.
                //
                // Measured on this repo's wiring: onBackgroundFetchComplete
                // (o2d-timeline.element.ts:1191) calls notifyDataAvailable, which increments
                // the state manager's version unconditionally
                // (data-provider-state-manager.ts:246); that version is a Lit @state, reaches
                // o2d-area as dataProviderVersion, and o2d-area re-queries the provider on
                // update (o2d-area.element.ts:250). The cycle closes with no user input.
                //
                // Leaving the flag alone means the entries that DID resolve are still in the
                // matrix and appear on the next render the host makes for its own reasons —
                // one render later, not never. The retry then happens when the user actually
                // scrolls, which is exactly what a failed fetchData already does (SKIP marks
                // nothing and fires no callback either).
                //
                // Deliberately NOT adding an attempt counter here. Without the callback this
                // path has the same cadence as the pre-existing SKIP path — one attempt per
                // genuine viewport query — so a cap would be a new policy for one of two
                // identical paths. If a cap is wanted, it belongs in FetchedRegionsTracker and
                // covers both.
                if (!hadUnexpectedFailure) {
                    this.fetchedRegionsTracker.markAsFetched(region, items.length === 0);
                    anySuccessfulFetch = true;
                }

            } catch (error) {
                // AbortError means a newer request superseded this one — skip this region cleanly
                if (error instanceof DOMException && error.name === 'AbortError') {
                    // Remove from in-flight set on abort (AC4)
                    this.inFlightRegions.delete(regionKey);
                    // Don't call callback, don't mark as fetched, continue with next region
                    continue;
                }

                // Handle other errors via error handler
                const action = this.errorHandler.onFetchError(region, error as Error);

                if (action === 'ABORT') {
                    // Remove from in-flight set before throwing
                    this.inFlightRegions.delete(regionKey);
                    throw error;
                }
                // SKIP: Continue with next region, but remove from in-flight
                this.inFlightRegions.delete(regionKey);
            } finally {
                // Always remove from in-flight set after fetch completes (AC7)
                this.inFlightRegions.delete(regionKey);
            }
        }

        // Call callback after completion if any region was successfully fetched (AC2, DE5)
        if (anySuccessfulFetch && this.onBackgroundFetchComplete) {
            this.onBackgroundFetchComplete();
        }
    }

    /**
     * Generate a unique region key for in-flight tracking
     * Format: "x{xMin}-{xMax}_y{yMin}-{yMax}"
     *
     * @param indexRangeX - X-axis index range
     * @param indexRangeY - Y-axis index range
     * @returns Deterministic region key
     */
    private generateRegionKey(indexRangeX: [number, number], indexRangeY: [number, number]): string {
        return `x${indexRangeX[0]}-${indexRangeX[1]}_y${indexRangeY[0]}-${indexRangeY[1]}`;
    }

    /**
     * Ensure all axis values referenced by matrix entries are loaded in the axis cache,
     * and return only the entries that are safe to add to the SpatialMatrix.
     *
     * Calls axis.ensureValue() for any missing value ONLY when the axis' service supports
     * it (canExpandToValue). An entry whose axis service lacks expandToValue for a missing
     * value cannot be loaded; adding it would make addItem throw "Values not found in axis
     * cache", merely relocating the crash. Such an entry is dropped from the returned list.
     *
     * The drop unit is the WHOLE entry (per-entry conjunction): an entry is safe-to-add IFF
     * all four values (xStart, xEnd, yStart, yEnd) — spanning two axis services — are already
     * cached or loadable. The partition is SYNCHRONOUS first (no wasted load on a doomed
     * entry), then the retained entries' ensureValue() calls are awaited PER ENTRY.
     *
     * A rejected ensureValue drops ONLY its own entry; it does NOT propagate. That is a
     * deliberate change from the earlier contract (rejection propagated as ABORT). The reason
     * is the caller: fetchRegionsInBackground() runs fire-and-forget without a .catch, and it
     * only reaches markAsFetched() on the success path. So a single unloadable value used to
     * abandon the whole region AND leave it unmarked — the next viewport query re-queued it,
     * producing one real backend request per scroll frame, forever. Dropping the one entry
     * keeps the region successful and the loop bounded.
     *
     * Every drop is reported, but ONCE PER CALL with a count and one example — not per entry.
     * A viewport with 200 unloadable entries would otherwise emit 200 lines per pass, and a
     * warning that is permanently present stops being read.
     *
     * A rejection that is NOT a ValueOutsideDomainError is additionally reported to the
     * configured error handler. The drop policy does not change — the entry goes either way and
     * the region stays successful — but without that report a systemic failure (backend
     * unreachable, timeouts, 5xx for every value) would look exactly like one harmless
     * out-of-range value: entries quietly gone, one console line, nothing reaching monitoring.
     * The handler's verdict is deliberately NOT acted on here; escalating it would reinstate
     * the abandoned-region refetch loop described above.
     *
     * @param entries - Matrix entries with raw axis values to check
     * @param region - The region being fetched, passed to the error handler on unexpected failures
     * @returns The entries safe to add, and whether any load failed for an UNEXPECTED reason.
     *   The caller uses the flag to decide whether the region may be marked as fetched: a
     *   value that is genuinely not in the domain means the region IS complete, but a backend
     *   that was unreachable means it is not — and marking it would lose those items for the
     *   session, since the region would never be requested again.
     */
    private async ensureIndicesComplete(
        entries: Array<MatrixEntry<TXValue, TYValue, TItem>>,
        region: Viewport
    ): Promise<{
        safeEntries: Array<MatrixEntry<TXValue, TYValue, TItem>>;
        hadUnexpectedFailure: boolean;
    }> {
        const xAxis = this.spatialMatrix.getXAxis();
        const yAxis = this.spatialMatrix.getYAxis();

        // Entries kept for loading, in input order, each with its own load promises. The
        // ensureValue() calls are created HERE, inside the loop, so every one of them still
        // snapshots the same axis boundaries before any insert happens — that synchronous
        // window is what makes concurrent expansions consistent.
        // Each load carries the value it is loading, so a rejection can name the value that
        // ACTUALLY failed. Reporting a fixed field (say spanX[0]) would point at a value that
        // may well have loaded fine while the failing one never appears — and the whole point
        // of the warning is to aim the next investigation correctly.
        const candidates: Array<{
            entryIndex: number;
            entry: MatrixEntry<TXValue, TYValue, TItem>;
            loads: Array<{ label: string; promise: Promise<number> }>;
        }> = [];

        let droppedUnsupported = 0;
        let droppedFailed = 0;

        // Keyed by the entry's position in `entries`, not by assignment time. The two drop
        // paths run in different phases — unsupported entries are recognised synchronously in
        // the loop below, failed loads only after the await — so a plain "first assignment
        // wins" would report a later unsupported entry ahead of an earlier failed one and
        // contradict the "first affected" the warning claims to name.
        //
        // Kept PER CATEGORY as well: the console line names the first affected value across
        // all drops, but a report that says "expandToValue is not implemented" must name a
        // value from THAT category. Naming a value whose axis does implement the method — it
        // was simply outside the domain — would send the reader to look for a missing method
        // that is right there.
        const droppedValueByIndex = new Map<number, string>();
        const unsupportedValueByIndex = new Map<number, string>();

        for (const [entryIndex, entry] of entries.entries()) {
            const [xStart, xEnd] = entry.spanX;
            const [yStart, yEnd] = entry.indexRangeY;

            // Synchronous per-entry safety check (no await): an entry is droppable iff either
            // axis has a missing value that its service cannot expand to. Drop the whole entry.
            const xMissing = !xAxis.hasValue(xStart) || !xAxis.hasValue(xEnd);
            const yMissing = !yAxis.hasValue(yStart) || !yAxis.hasValue(yEnd);
            const droppable =
                (xMissing && !xAxis.canExpandToValue) ||
                (yMissing && !yAxis.canExpandToValue);
            if (droppable) {
                droppedUnsupported++;
                // Name a value on the axis that actually cannot expand — not merely the first
                // missing value of the entry. With a missing value on BOTH axes but only Y
                // unable to expand, naming the X value would send the next investigation to
                // the axis that was never the problem.
                const blockedOnX = xMissing && !xAxis.canExpandToValue;
                const unsupportedValue = describeValue(
                    blockedOnX
                        ? (!xAxis.hasValue(xStart) ? xStart : xEnd)
                        : (!yAxis.hasValue(yStart) ? yStart : yEnd)
                );
                droppedValueByIndex.set(entryIndex, unsupportedValue);
                unsupportedValueByIndex.set(entryIndex, unsupportedValue);
                continue;
            }

            const loads: Array<{ label: string; promise: Promise<number> }> = [];
            if (!xAxis.hasValue(xStart)) {
                loads.push({label: describeValue(xStart), promise: xAxis.ensureValue(xStart)});
            }
            if (!xAxis.hasValue(xEnd)) {
                loads.push({label: describeValue(xEnd), promise: xAxis.ensureValue(xEnd)});
            }
            if (!yAxis.hasValue(yStart)) {
                loads.push({label: describeValue(yStart), promise: yAxis.ensureValue(yStart)});
            }
            if (!yAxis.hasValue(yEnd)) {
                loads.push({label: describeValue(yEnd), promise: yAxis.ensureValue(yEnd)});
            }

            candidates.push({entryIndex, entry, loads});
        }

        // Per entry: settle all its loads, then look up WHICH one rejected. allSettled never
        // rejects, so the outer Promise.all is safe, and it preserves order — safeEntries keeps
        // the input order the matrix expects.
        const outcomes = await Promise.all(candidates.map(async candidate => {
            const results = await Promise.allSettled(candidate.loads.map(load => load.promise));

            // A plain loop rather than findIndex + cast: this way the compiler narrows the
            // result itself, so nothing here rests on an assertion that a later refactor
            // (a sort, a filter) could quietly invalidate.
            for (const [index, result] of results.entries()) {
                if (result.status === 'rejected') {
                    return {
                        loaded: false,
                        failedValue: candidate.loads[index].label,
                        reason: result.reason as unknown
                    };
                }
            }

            return {loaded: true, failedValue: undefined, reason: undefined};
        }));

        const safeEntries: Array<MatrixEntry<TXValue, TYValue, TItem>> = [];
        let droppedUnexpected = 0;
        let firstUnexpectedReason: unknown;

        outcomes.forEach((outcome, index) => {
            if (outcome.loaded) {
                safeEntries.push(candidates[index].entry);
                return;
            }

            droppedFailed++;
            if (outcome.failedValue !== undefined) {
                droppedValueByIndex.set(candidates[index].entryIndex, outcome.failedValue);
            }

            // "Outside the domain" is the expected answer and stays a local matter. Anything
            // else is a malfunction the consumer must be able to see.
            if (!ValueOutsideDomainError.is(outcome.reason)) {
                droppedUnexpected++;
                firstUnexpectedReason ??= outcome.reason;
            }
        });

        const droppedTotal = droppedUnsupported + droppedFailed;

        // Lowest entry index wins, so "first affected" means first in the input, not first
        // to be recognised.
        const firstIn = (byIndex: Map<number, string>): string | undefined =>
            byIndex.size > 0
                ? byIndex.get([...byIndex.keys()].reduce((min, key) => Math.min(min, key), Infinity))
                : undefined;

        const firstAffectedValue = firstIn(droppedValueByIndex);

        if (droppedTotal > 0) {
            console.warn(
                `[CrosspointDataProvider] Dropped ${droppedTotal} of ${entries.length} entries: ` +
                `axis values could not be loaded ` +
                `(${droppedUnsupported} unsupported by the axis service, ${droppedFailed} failed to load, ` +
                `${droppedUnexpected} of those for an unexpected reason). ` +
                `First affected value: ${firstAffectedValue}. ` +
                `These items will not be rendered.`
            );
        }

        // Everything a consumer's monitoring should learn about goes through the handler. The
        // console line alone is not reach: this whole change exists because entries were
        // disappearing where nobody was looking.
        //
        // Two reportable cases, deliberately separate from the third:
        //   - an axis service without expandToValue at all. A CAPABILITY gap, not a failure —
        //     retrying cannot help, so the region still counts as complete — but it is exactly
        //     the situation that produced this defect in the first place, and the next service
        //     that forgets the method must not disappear as quietly as the last one did.
        //   - a load that failed for an unexpected reason (handled above via the flag: the
        //     region is NOT marked, so it is retried).
        // The third case, a value genuinely outside the axis' domain, stays local: it is a
        // statement about the data, not something to alert on.
        // ONE report per region, even when both kinds of drop occur in the same pass. Two
        // calls would make a handler that counts events, fires an alert or feeds a circuit
        // breaker see one incident as two.
        //
        // But one report must not mean one of the two facts is thrown away, and an earlier
        // revision did exactly that: with both kinds present, only the unexpected failure was
        // reported and the capability gap vanished for the handler — the very signal this
        // change exists to raise. The two are not alternatives, and they co-occur for an
        // ordinary reason: expandToValue is a per-CLASS capability, so an axis service missing
        // it strands every entry needing that axis, while a single unrelated load on the other
        // axis can fail in the same pass.
        //
        // So when both happen, the unexpected failure is still the reported error — it is the
        // actionable one and it carries the original stack — but it is wrapped so the message
        // names the capability gap too, with the original kept reachable as `cause`. When only
        // the unexpected failure occurs, the error is passed through untouched, because
        // wrapping an error a handler may want to match on has a cost and there is nothing to
        // add.
        if (droppedUnexpected > 0) {
            const reported = firstUnexpectedReason instanceof Error
                ? firstUnexpectedReason
                : new Error(describeValue(firstUnexpectedReason));

            this.reportDroppedEntries(region, droppedUnsupported > 0
                ? new Error(
                    `[CrosspointDataProvider] ${droppedUnsupported} entries dropped because the ` +
                    `axis service cannot expand to their values (expandToValue is not ` +
                    `implemented; first affected value: ${firstIn(unsupportedValueByIndex)}), ` +
                    `alongside ${droppedUnexpected} that failed unexpectedly. See cause.`,
                    {cause: reported}
                )
                : reported);
        } else if (droppedUnsupported > 0) {
            this.reportDroppedEntries(region, new Error(
                `[CrosspointDataProvider] ${droppedUnsupported} entries dropped: the axis service ` +
                `cannot expand to their values (expandToValue is not implemented). ` +
                `First affected value: ${firstIn(unsupportedValueByIndex)}.`
            ));
        }

        return {safeEntries, hadUnexpectedFailure: droppedUnexpected > 0};
    }

    /**
     * Tells the error handler that entries were dropped from an otherwise successful region.
     *
     * Only the dedicated `onEntriesDropped` is used, with NO fallback to `onFetchError`.
     * Falling back would hand a non-error to a method whose contract says its verdict is
     * acted on: a consumer counting fetch errors would count harmless partial drops, and an
     * ABORT it returned would be discarded without it knowing. A handler that has not
     * implemented the method is exactly as informed as before it existed — the drop was
     * silent then too — and nothing it already relies on shifts meaning underneath it.
     *
     * @param region - The region the entries belonged to
     * @param error - What prevented the values from loading
     */
    private reportDroppedEntries(region: Viewport, error: Error): void {
        this.errorHandler.onEntriesDropped?.(region, error);
    }

    /**
     * Invalidate a cached item by ID and mark its region as unfetched.
     *
     * Removes all items matching the given issue UID (exact or composite prefix `{issueUidPrefix}_*`)
     * from SpatialMatrix and invalidates each removed item's region via region-subtraction.
     *
     * @param issueUidPrefix - The issue UID (or prefix for composite IDs) to invalidate
     */
    invalidateItem(issueUidPrefix: string): void {
        const removedItems = this.spatialMatrix.removeItemsByPrefix(issueUidPrefix);

        for (const item of removedItems) {
            this.fetchedRegionsTracker.invalidateRegion({
                indexRangeX: item.indexRangeX,
                indexRangeY: item.indexRangeY,
            });
        }
    }

    /**
     * Mark a region as unfetched in the FetchedRegionsTracker.
     *
     * Forces re-fetch of a specific region on the next viewport query.
     * Does NOT remove items from the SpatialMatrix — only affects fetch tracking.
     *
     * @param viewport - The region to invalidate (numeric indices)
     */
    invalidateRegion(viewport: Viewport): void {
        this.fetchedRegionsTracker.invalidateRegion(viewport);
    }

    /**
     * Cancel all pending requests
     */
    cancelPendingRequests(): void {
        this.dataService.cancelPendingRequests();
    }

    /**
     * Clear cache
     */
    clearCache(): void {
        this.spatialMatrix.clear();
        this.fetchedRegionsTracker.clear();
        this.inFlightRegions.clear(); // T2.5.4: Clear in-flight tracking (AC8)
    }

    /**
     * Dispose of resources
     */
    dispose(): void {
        this.clearCache();
        this.cancelPendingRequests();
    }

    /**
     * Retrieves the specified axis from the spatial matrix.
     *
     * @param {('x' | 'y')} axis - The axis to retrieve. Accepts 'x' for the TXAxisValue-axis or 'y' for the TYAxisValue-axis.
     * @return {Axis<TXValue> | Axis<TYValue>} The corresponding axis object from the matrix.
     */
    getAxis(axis: 'x' | 'y'): Axis<TXValue> | Axis<TYValue> {
        return axis === 'x' ? this.spatialMatrix.getXAxis() : this.spatialMatrix.getYAxis();
    }
}
